import React, { useState, useEffect, useRef } from 'react';
import NavBar from '../../../components/NavBar';
import Cookies from 'js-cookie';
import ReactECharts from 'echarts-for-react';
import IndicadoresTab from './IndicadoresTab';
import { AiOutlineFileExcel } from 'react-icons/ai';
import DoencasAgravos from './DoencasAgravos';
import { FaArrowRight } from 'react-icons/fa';
import { FaArrowLeft } from 'react-icons/fa';
import { FiChevronDown, FiSliders } from 'react-icons/fi';

import {
	requestOcupacaoLeitosOptions,
	requestOcupacaoLeitosHistoricoOptions,
	requestOcupationdeLeitos,
	requestOcupationdeLeitosGrafico,
	requestOcupationdeLeitosExcel,
	requestOcupationdeLeitosGraficoExcel,
} from '../../../services/requests';
import excelExportIcon from '../../../images/excelExport.png';

const formatTaxaOcupacao = (value) => {
	const numericValue = Number(value);
	if (!Number.isFinite(numericValue)) return '--';

	return new Intl.NumberFormat('pt-BR', {
		maximumFractionDigits: 1,
	}).format(numericValue);
};

// O endpoint histórico devolve uma linha por setor/unidade. Para a única série
// exibida nesta tela, linhas com o mesmo período representam o mesmo ponto do
// gráfico e precisam ser consolidadas sem criar datas artificiais.
export const buildHistoricalChartPoints = (serie = []) => {
	const pointsByPeriod = new Map();

	(Array.isArray(serie) ? serie : []).forEach((item = {}) => {
		const periodoLabel = String(
			item?.periodo_label ?? item?.periodo_inicio ?? '',
		).trim();
		if (!periodoLabel) return;

		const existing = pointsByPeriod.get(periodoLabel) || {
			periodo_label: periodoLabel,
			periodo_inicio: item?.periodo_inicio,
			periodo_fim: item?.periodo_fim,
			soma_taxas: 0,
			quantidade_taxas: 0,
			soma_ponderada: 0,
			soma_pesos: 0,
		};
		const taxa = Number(item?.taxa_ocupacao_hospitalar);
		const peso = Number(item?.leitos_operacionais);

		if (Number.isFinite(taxa)) {
			existing.soma_taxas += taxa;
			existing.quantidade_taxas += 1;
			if (Number.isFinite(peso) && peso > 0) {
				existing.soma_ponderada += taxa * peso;
				existing.soma_pesos += peso;
			}
		}

		pointsByPeriod.set(periodoLabel, existing);
	});

	return Array.from(pointsByPeriod.values()).map((point) => ({
		periodo_label: point.periodo_label,
		periodo_inicio: point.periodo_inicio,
		periodo_fim: point.periodo_fim,
		taxa_ocupacao_hospitalar:
			point.soma_pesos > 0
				? point.soma_ponderada / point.soma_pesos
				: point.quantidade_taxas > 0
					? point.soma_taxas / point.quantidade_taxas
					: null,
	}));
};

const formatLocalISODate = (date) => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

export const getDefaultHistoricalDateRange = (agregacao = 'dia') => {
	const today = new Date();
	const dataFim = formatLocalISODate(today);

	if (agregacao === 'mes') {
		// Três competências incluindo o mês atual.
		const inicioTresMeses = new Date(
			today.getFullYear(),
			today.getMonth() - 2,
			1,
		);
		return {
			dataInicio: formatLocalISODate(inicioTresMeses),
			dataFim,
		};
	}

	const inicioSeteDiasAtras = new Date(
		today.getFullYear(),
		today.getMonth(),
		today.getDate() - 7,
	);
	return {
		dataInicio: formatLocalISODate(inicioSeteDiasAtras),
		dataFim,
	};
};

export const getHistoricalApiAlert = (response = {}) => {
	const payload = response?.error ?? response;
	if (typeof payload === 'string') return payload;
	if (!payload || typeof payload !== 'object') return '';

	const candidates = [
		payload?.periodo,
		payload?.alerta,
		payload?.alertas,
		payload?.mensagem,
		payload?.message,
		payload?.detail,
		payload?.erro,
		payload?.errors,
	];

	for (const candidate of candidates) {
		if (typeof candidate === 'string' && candidate.trim()) {
			return candidate.trim();
		}
		if (Array.isArray(candidate)) {
			const message = candidate
				.map((item) =>
					typeof item === 'string'
						? item
						: item?.mensagem || item?.message || item?.detail || '',
				)
				.filter(Boolean)
				.join(' ');
			if (message) return message;
		}
		if (candidate && typeof candidate === 'object') {
			const message =
				candidate?.mensagem || candidate?.message || candidate?.detail;
			if (typeof message === 'string' && message.trim()) {
				return message.trim();
			}
		}
	}

	return (
		Object.values(payload).find(
			(value) => typeof value === 'string' && value.trim(),
		) || ''
	);
};

const normalizeFacetOptions = (value) => {
	if (Array.isArray(value)) return value;

	const optionCollections = [
		value?.opcoes,
		value?.options,
		value?.valores,
		value?.values,
		value?.itens,
		value?.items,
		value?.choices,
		value?.results,
		value?.data,
	];
	const collection = optionCollections.find(Array.isArray);
	if (collection) return collection;

	if (value && typeof value === 'object') {
		const values = Object.values(value);
		if (values.length && values.every((item) => typeof item === 'string')) {
			return values;
		}
	}

	return [];
};

const getHistoricalFacetPayload = (response) => {
	const expectedFacets = [
		'area_programatica',
		'superintendencia',
		'unidade',
		'tipo_secao',
		'setor',
	];
	const payload = response?.data || response || {};
	const nestedOptions = payload.campos;

	if (
		nestedOptions &&
		typeof nestedOptions === 'object' &&
		!Array.isArray(nestedOptions) &&
		expectedFacets.some((facet) => facet in nestedOptions)
	) {
		return nestedOptions;
	}

	if (Array.isArray(nestedOptions)) {
		return nestedOptions.reduce((facets, item) => {
			const field = item?.campo || item?.field || item?.nome || item?.id;
			if (expectedFacets.includes(field)) {
				facets[field] = normalizeFacetOptions(item);
			}
			return facets;
		}, {});
	}

	return payload;
};

const SearchableMultiSelect = ({
	label,
	options,
	selectedOptions,
	searchTerm,
	isOpen,
	onOpen,
	onClose,
	onSearchChange,
	onToggleOption,
	labelClassName,
}) => {
	const containerRef = useRef(null);
	const normalizedOptions = normalizeFacetOptions(options);
	const filteredOptions = normalizedOptions.filter((option) =>
		String(option).toLocaleLowerCase('pt-BR').includes(
			searchTerm.toLocaleLowerCase('pt-BR').trim(),
		),
	);
	const selectedLabel = selectedOptions.length
		? `${selectedOptions.length} selecionado${selectedOptions.length > 1 ? 's' : ''}`
		: '';

	useEffect(() => {
		if (!isOpen) return undefined;

		const closeWhenClickingOutside = (event) => {
			if (!containerRef.current?.contains(event.target)) onClose();
		};

		document.addEventListener('mousedown', closeWhenClickingOutside);
		return () =>
			document.removeEventListener('mousedown', closeWhenClickingOutside);
	}, [isOpen, onClose]);

	return (
		<div ref={containerRef} className='relative min-w-[200px] flex-1'>
			<label className={labelClassName}>{label}</label>
			<div className='relative'>
				<input
					type='text'
					value={searchTerm}
					onFocus={onOpen}
					onClick={onOpen}
					onBlur={() => window.setTimeout(onClose, 120)}
					onChange={(event) => {
						onSearchChange(event.target.value);
						onOpen();
					}}
					placeholder={
						selectedLabel
							? `${selectedLabel} — buscar ${label.toLocaleLowerCase('pt-BR')}`
							: `Buscar ${label.toLocaleLowerCase('pt-BR')}`
					}
					className='w-full rounded-md border border-gray-300 bg-white px-3 py-2 pr-9 text-sm text-[#4F5257] shadow-sm outline-none placeholder:text-[#9CA3AF] focus:border-[#13335A] focus:ring-1 focus:ring-[#13335A]'
					aria-label={label}
				/>
				<button
					type='button'
					onMouseDown={(event) => event.preventDefault()}
					onClick={() => (isOpen ? onClose() : onOpen())}
					className='absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded hover:bg-[#F7FAFE]'
					aria-label={isOpen ? `Fechar ${label}` : `Abrir ${label}`}>
					<FiChevronDown
						className={`h-4 w-4 text-[#4F5257] transition-transform ${
							isOpen ? 'rotate-180' : ''
						}`}
						aria-hidden='true'
					/>
				</button>
			</div>
			{isOpen && (
				<div className='absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white p-2 shadow-lg'>
					{filteredOptions.length ? (
						filteredOptions.map((option) => (
							<label
								key={option}
								onMouseDown={(event) => event.preventDefault()}
								className='flex min-w-0 cursor-pointer items-center gap-2 rounded px-2 py-2 text-sm text-[#4F5257] hover:bg-gray-50'>
								<input
									type='checkbox'
									checked={selectedOptions.includes(option)}
									onChange={() => onToggleOption(option)}
									className='h-4 w-4 flex-none accent-[#13335A]'
								/>
								<span className='max-h-10 overflow-hidden break-words leading-5'>
									{option}
								</span>
							</label>
						))
					) : (
						<p className='px-2 py-2 text-sm text-gray-400'>
							Nenhuma opção disponível
						</p>
					)}
				</div>
			)}
		</div>
	);
};

const SearchablePageSizeSelect = ({ value, onChange }) => {
	const containerRef = useRef(null);
	const [isOpen, setIsOpen] = useState(false);
	const [query, setQuery] = useState(String(value));
	const options = [10, 20, 50, 100];
	const filteredOptions = options.filter((option) =>
		String(option).includes(query.trim()),
	);

	useEffect(() => setQuery(String(value)), [value]);

	useEffect(() => {
		if (!isOpen) return undefined;

		const closeWhenClickingOutside = (event) => {
			if (!containerRef.current?.contains(event.target)) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', closeWhenClickingOutside);
		return () =>
			document.removeEventListener('mousedown', closeWhenClickingOutside);
	}, [isOpen]);

	return (
		<div ref={containerRef} className='relative'>
			<input
				type='text'
				value={query}
				onFocus={() => setIsOpen(true)}
				onClick={() => setIsOpen(true)}
				onBlur={() => window.setTimeout(() => setIsOpen(false), 120)}
				onChange={(event) => {
					setQuery(event.target.value);
					setIsOpen(true);
				}}
				className='min-w-[58px] rounded-md border border-gray-300 px-2 py-1 pr-6 text-xs outline-none focus:border-[#13335A] sm:text-sm'
				aria-label='Itens por página'
			/>
			<button
				type='button'
				onMouseDown={(event) => event.preventDefault()}
				onClick={() => setIsOpen((previous) => !previous)}
				className='absolute right-0 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded hover:bg-[#F7FAFE]'
				aria-label={isOpen ? 'Fechar opções por página' : 'Abrir opções por página'}>
				<FiChevronDown
					className={`h-3.5 w-3.5 transition-transform ${
						isOpen ? 'rotate-180' : ''
					}`}
					aria-hidden='true'
				/>
			</button>
			{isOpen && (
				<div className='absolute bottom-full left-0 z-30 mb-1 min-w-full rounded-md border border-gray-200 bg-white p-1 shadow-lg sm:bottom-auto sm:top-full sm:mt-1 sm:mb-0'>
					{filteredOptions.map((option) => (
						<button
							key={option}
							type='button'
							onMouseDown={(event) => {
								event.preventDefault();
								onChange(option);
								setIsOpen(false);
								setQuery(String(option));
							}}
							className='block w-full rounded px-2 py-1 text-left text-xs hover:bg-[#F7FAFE] sm:text-sm'>
							{option}
						</button>
					))}
					{!filteredOptions.length && (
						<p className='px-2 py-1 text-xs text-gray-400'>
							Nenhuma opção disponível
						</p>
					)}
				</div>
			)}
		</div>
	);
};

function MonitoramentoSUBHUE() {
	const [openTooltip, setOpenTooltip] = useState(null);
	const [showAlert, setShowAlert] = useState(false);
	const [isAccessChecked, setIsAccessChecked] = useState(false);
	const [activeTab, setActiveTab] = useState('ocupacao');
	const [selectedUnidade, setSelectedUnidade] = useState('');
	const [selectedEnfermaria, setSelectedEnfermaria] = useState('');
	const [selectedTipo, setSelectedTipo] = useState('');
	const [selectedAreaProgramatica, setSelectedAreaProgramatica] =
		useState('');
	const [selectedSuperintendencia, setSelectedSuperintendencia] =
		useState('');

	const [filterOptions, setFilterOptions] = useState({
		areas_programaticas: [],
		superintendencias: [],
		unidades: [],
		setores: [],
		tipos: [],
	});

	const [availableSuperintendencias, setAvailableSuperintendencias] =
		useState([]);
	const [availableUnidades, setAvailableUnidades] = useState([]);
	const [availableSeccoes, setAvailableSeccoes] = useState([]);
	const [availableEnfermarias, setAvailableEnfermarias] = useState([]);

	const [availableUnidade, setAvailableUnidade] = useState([]);

	const [availabletipo, setAvailabletipo] = useState([]);
	const [ocupacaoData, setOcupacaoData] = useState({
		registros: [],
		cards: {},
		ultima_atualizacao: '',
	});
	const [loading, setLoading] = useState(false);
	const [serieData, setSerieData] = useState({
		serie: [],
		agregacao: 'dia',
		ultima_atualizacao: null,
	});
	const [loadingSerie, setLoadingSerie] = useState(false);
	const [historicalAlert, setHistoricalAlert] = useState('');

	const [initialHistoricalDateRange] = useState(() =>
		getDefaultHistoricalDateRange('dia'),
	);
	const [dataInicio, setDataInicio] = useState(
		initialHistoricalDateRange.dataInicio,
	);
	const [dataFim, setDataFim] = useState(
		initialHistoricalDateRange.dataFim,
	);
	const [isHistoricalPeriodDefault, setIsHistoricalPeriodDefault] =
		useState(true);

	const [agregacao, setAgregacao] = useState('dia');
	const [exportingSerie, setExportingSerie] = useState(false);
	const [exportingTabela, setExportingTabela] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(10);
	const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
	const [clearButton, setClearButton] = useState(true);
	const [selectedVisualFilters, setSelectedVisualFilters] = useState({
		areaProgramatica: [],
		superintendencia: [],
		unidade: [],
		tipo: [],
		enfermaria: [],
	});
	const [openVisualFilter, setOpenVisualFilter] = useState(null);
	const [visualFilterSearch, setVisualFilterSearch] = useState({});
	const visualOptionsRequestRef = useRef(0);
	const [historicalFilterOptions, setHistoricalFilterOptions] = useState({
		area_programatica: [],
		superintendencia: [],
		unidade: [],
		tipo_secao: [],
		setor: [],
	});
	const [selectedHistoricalFilters, setSelectedHistoricalFilters] =
		useState({
			area_programatica: [],
			superintendencia: [],
			unidade: [],
			tipo_secao: [],
			setor: [],
		});
	const [appliedHistoricalFilters, setAppliedHistoricalFilters] =
		useState({
			area_programatica: [],
			superintendencia: [],
			unidade: [],
			tipo_secao: [],
			setor: [],
		});
	const [openHistoricalFilter, setOpenHistoricalFilter] = useState(null);
	const [historicalFilterSearch, setHistoricalFilterSearch] = useState({});
	const [isHistoricalFiltersOpen, setIsHistoricalFiltersOpen] =
		useState(true);
	const historicalOptionsRequestRef = useRef(0);

	const toggleVisualFilterOption = (filter, option) => {
		const selectedOptions = selectedVisualFilters[filter] || [];
		const isSelected = selectedOptions.includes(option);
		const nextFilters = {
			...selectedVisualFilters,
			[filter]: isSelected
				? selectedOptions.filter((value) => value !== option)
				: [...selectedOptions, option],
		};

		setSelectedVisualFilters(nextFilters);
		loadFilterOptions(nextFilters);
	};

	const toggleHistoricalFilterOption = (filter, option) => {
		const selectedOptions = selectedHistoricalFilters[filter] || [];
		const isSelected = selectedOptions.includes(option);
		const nextFilters = {
			...selectedHistoricalFilters,
			[filter]: isSelected
				? selectedOptions.filter((value) => value !== option)
				: [...selectedOptions, option],
		};

		setSelectedHistoricalFilters(nextFilters);
		loadHistoricoFilterOptions({ filters: nextFilters });
	};

	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth < 640);
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	useEffect(() => {
		const localData = Cookies.get('user');

		if (localData) {
			try {
				const user = JSON.parse(localData);
				if (
					user.unidade &&
					user.unidade.toLowerCase().includes('subhue')
				) {
					setShowAlert(false);
				} else {
					setShowAlert(true);
				}
			} catch (error) {
				console.error('Erro ao processar dados do usuário:', error);
				setShowAlert(true);
			}
		} else {
			setShowAlert(true);
		}

		setIsAccessChecked(true);
	}, []);

	const loadFilterOptions = async (
		filterSelection = selectedVisualFilters,
	) => {
		try {
			const userCookie = Cookies.get('user');
			const user = userCookie ? JSON.parse(userCookie) : null;
			const token = user?.token;
			if (!token) return;
			const currentFilters =
				filterSelection && typeof filterSelection === 'object'
					? filterSelection
					: selectedVisualFilters;
			const filterMapping = {
				areaProgramatica: 'area_programatica',
				superintendencia: 'superintendencia',
				unidade: 'unidade',
				tipo: 'tipo_secao',
				enfermaria: 'setor',
			};
			const params = Object.entries(filterMapping).reduce(
				(filters, [stateKey, apiKey]) => {
					if (Array.isArray(currentFilters[stateKey]) && currentFilters[stateKey].length) {
						filters[apiKey] = currentFilters[stateKey];
					}
					return filters;
				},
				{},
			);
			const requestId = ++visualOptionsRequestRef.current;

			const response = await requestOcupacaoLeitosOptions(token, params);
			if (requestId !== visualOptionsRequestRef.current) return;

			if (response && !response.error) {
				const options = getHistoricalFacetPayload(response);
				const getFacetOptions = (facet) => {
					const nestedFacetOptions = normalizeFacetOptions(options[facet]);
					return nestedFacetOptions.length
						? nestedFacetOptions
						: normalizeFacetOptions(response[facet]);
				};
				const areaProgramaticaOptions = getFacetOptions('area_programatica');
				const superintendenciaOptions = getFacetOptions('superintendencia');
				const unidadeOptions = getFacetOptions('unidade');
				const tipoOptions = getFacetOptions('tipo_secao');
				const setorOptions = getFacetOptions('setor');

				setFilterOptions({
					areas_programaticas: areaProgramaticaOptions,
					superintendencias: superintendenciaOptions,
					unidades: unidadeOptions,
					setores: setorOptions,
					tipos: tipoOptions,
				});
				setAvailableSuperintendencias(superintendenciaOptions);
				setAvailableUnidades(unidadeOptions);
				setAvailableSeccoes(tipoOptions);
				setAvailableEnfermarias(setorOptions);
			}
		} catch (err) {
			console.error('Erro ao carregar filtros:', err);
		}
	};

	const loadHistoricoFilterOptions = async (config = {}) => {
		try {
			const userCookie = Cookies.get('user');
			const user = userCookie ? JSON.parse(userCookie) : null;
			const token = user?.token;
			if (!token) return;
			const currentFilters = config.filters ?? selectedHistoricalFilters;
			const currentAgregacao = config.agregacao ?? agregacao;
			const currentDataInicio = config.dataInicio ?? dataInicio;
			const currentDataFim = config.dataFim ?? dataFim;
			const params = { agregacao: currentAgregacao };

			if (currentDataInicio) params.data_inicio = currentDataInicio;
			if (currentDataFim) params.data_fim = currentDataFim;
			Object.entries(currentFilters).forEach(([filter, values]) => {
				if (Array.isArray(values) && values.length) {
					params[filter] = values;
				}
			});

			const requestId = ++historicalOptionsRequestRef.current;

			const response = await requestOcupacaoLeitosHistoricoOptions(
				token,
				params,
			);

			if (requestId !== historicalOptionsRequestRef.current) return;

			if (response && !response.error) {
				const options = getHistoricalFacetPayload(response);
				const getFacetOptions = (facet) => {
					const nestedFacetOptions = normalizeFacetOptions(
						options[facet],
					);
					return nestedFacetOptions.length
						? nestedFacetOptions
						: normalizeFacetOptions(response[facet]);
				};
				setHistoricalFilterOptions({
					area_programatica: getFacetOptions('area_programatica'),
					superintendencia: getFacetOptions('superintendencia'),
					unidade: getFacetOptions('unidade'),
					tipo_secao: getFacetOptions('tipo_secao'),
					setor: getFacetOptions('setor'),
				});
			} else {
				setHistoricalAlert(getHistoricalApiAlert(response));
			}
		} catch (error) {
			console.error(
				'Erro ao carregar filtros da série histórica:',
				error,
			);
		}
	};

	const loadOcupacaoData = async (filters = {}, page = 1, pageSize = 10) => {
		try {
			const userCookie = Cookies.get('user');
			const user = userCookie ? JSON.parse(userCookie) : null;
			const token = user?.token;

			if (!token) {
				console.error('Token não encontrado');
				return;
			}

			// Adicionar parâmetros de paginação aos filtros
			const paginatedFilters = {
				...filters,
				page: page,
				page_size: pageSize,
			};

			const response = await requestOcupationdeLeitos(
				paginatedFilters,
				token,
			);

			if (response && !response.error) {
				setOcupacaoData(response);
			}
		} catch (error) {
			console.error('Erro ao carregar dados de ocupação:', error);
		} finally {
		}
	};

	const getTableFilters = () => {
		const filterMapping = {
			areaProgramatica: 'area_programatica',
			superintendencia: 'superintendencia',
			unidade: 'unidade',
			tipo: 'tipo_secao',
			enfermaria: 'setor',
		};

		return Object.entries(filterMapping).reduce(
			(filters, [stateKey, apiKey]) => {
				if (selectedVisualFilters[stateKey].length) {
					filters[apiKey] = selectedVisualFilters[stateKey];
				}
				return filters;
			},
			{},
		);
	};

	const handleApplyFilters = () => {
		const filters = getTableFilters();

		setCurrentPage(1); // Resetar para primeira página ao aplicar filtros
		loadOcupacaoData(filters, 1, itemsPerPage);
	};
	const handleClearFilters = () => {
		const emptyVisualFilters = {
			areaProgramatica: [],
			superintendencia: [],
			unidade: [],
			tipo: [],
			enfermaria: [],
		};
		setSelectedVisualFilters(emptyVisualFilters);
		setOpenVisualFilter(null);
		setVisualFilterSearch({});
		setSelectedUnidade('');
		setSelectedSuperintendencia('');
		setSelectedAreaProgramatica('');
		setSelectedEnfermaria('');
		setSelectedTipo('');
		setCurrentPage(1); // Resetar página
		loadFilterOptions(emptyVisualFilters);
		loadOcupacaoData({}, 1, itemsPerPage);
	};

	const loadSerieHistorica = async (
		clear,
		filterSelection = selectedHistoricalFilters,
		config = {},
	) => {
		try {
			setLoadingSerie(true);
			setHistoricalAlert('');
			const userCookie = Cookies.get('user');
			const user = userCookie ? JSON.parse(userCookie) : null;
			const token = user?.token;
			if (!token) {
				console.error('Token não encontrado');
				return;
			}
			const filters = {};
			const currentDataInicio = config.dataInicio ?? dataInicio;
			const currentDataFim = config.dataFim ?? dataFim;
			const currentAgregacao = config.agregacao ?? agregacao;

			Object.entries(filterSelection).forEach(
				([filter, values]) => {
					if (values.length) filters[filter] = values;
				},
			);
			if (currentDataInicio && !clear)
				filters.data_inicio = currentDataInicio;
			if (currentDataFim && !clear) filters.data_fim = currentDataFim;
			if (currentAgregacao && !clear)
				filters.agregacao = currentAgregacao;

			const response = await requestOcupationdeLeitosGrafico(
				clear === 'general' ? {} : filters,
				token,
			);

			if (response && !response.error && Array.isArray(response.serie)) {
				setSerieData(response);
			} else {
				setHistoricalAlert(getHistoricalApiAlert(response));
			}
		} catch (error) {
			console.error('Erro ao carregar série histórica:', error);
			setHistoricalAlert(getHistoricalApiAlert({ error }));
		} finally {
			setLoadingSerie(false);
		}
	};

	const handleApplySerieFilters = () => {
		setOpenHistoricalFilter(null);
		setAppliedHistoricalFilters(selectedHistoricalFilters);
		loadSerieHistorica();
	};

	const handleClearSerieFilters = () => {
		const emptyFilters = {
			area_programatica: [],
			superintendencia: [],
			unidade: [],
			tipo_secao: [],
			setor: [],
		};
		setSelectedHistoricalFilters(emptyFilters);
		setAppliedHistoricalFilters(emptyFilters);
		setOpenHistoricalFilter(null);
		setHistoricalFilterSearch({});
		const defaultRange = getDefaultHistoricalDateRange('dia');
		setDataInicio(defaultRange.dataInicio);
		setDataFim(defaultRange.dataFim);
		setIsHistoricalPeriodDefault(true);
		setAgregacao('dia');
		loadHistoricoFilterOptions({
			filters: emptyFilters,
			dataInicio: defaultRange.dataInicio,
			dataFim: defaultRange.dataFim,
			agregacao: 'dia',
		});
		loadSerieHistorica(false, emptyFilters, {
			dataInicio: defaultRange.dataInicio,
			dataFim: defaultRange.dataFim,
			agregacao: 'dia',
		});
	};

	const removeHistoricalFilter = (filter, option) => {
		const nextFilters = {
			...appliedHistoricalFilters,
			[filter]: appliedHistoricalFilters[filter].filter(
				(value) => value !== option,
			),
		};
		setSelectedHistoricalFilters(nextFilters);
		setAppliedHistoricalFilters(nextFilters);
		loadHistoricoFilterOptions({ filters: nextFilters });
		loadSerieHistorica(undefined, nextFilters);
	};

	const resetHistoricalPeriod = () => {
		const defaultRange = getDefaultHistoricalDateRange(agregacao);
		setDataInicio(defaultRange.dataInicio);
		setDataFim(defaultRange.dataFim);
		setIsHistoricalPeriodDefault(true);
		loadHistoricoFilterOptions({
			dataInicio: defaultRange.dataInicio,
			dataFim: defaultRange.dataFim,
		});
		loadSerieHistorica(undefined, selectedHistoricalFilters, {
			dataInicio: defaultRange.dataInicio,
			dataFim: defaultRange.dataFim,
		});
	};

	const resetHistoricalAggregation = () => {
		const defaultRange = isHistoricalPeriodDefault
			? getDefaultHistoricalDateRange('dia')
			: { dataInicio, dataFim };
		setAgregacao('dia');
		setDataInicio(defaultRange.dataInicio);
		setDataFim(defaultRange.dataFim);
		loadHistoricoFilterOptions({
			dataInicio: defaultRange.dataInicio,
			dataFim: defaultRange.dataFim,
			agregacao: 'dia',
		});
		loadSerieHistorica(undefined, selectedHistoricalFilters, {
			dataInicio: defaultRange.dataInicio,
			dataFim: defaultRange.dataFim,
			agregacao: 'dia',
		});
	};

	const handleHistoricalAggregationChange = (value) => {
		setHistoricalAlert('');
		setAgregacao(value);
		loadHistoricoFilterOptions({ agregacao: value });
	};

	const historicalFilterLabels = {
		area_programatica: 'Área Programática',
		superintendencia: 'Superintendência',
		unidade: 'Unidade',
		tipo_secao: 'Tipos',
		setor: 'Enfermarias',
	};
	const historicalFilterChips = Object.entries(appliedHistoricalFilters).flatMap(
		([filter, values]) =>
			values.map((value) => ({
				filter,
				value,
				label: `${historicalFilterLabels[filter]}: ${value}`,
			})),
	);
	const activeHistoricalFiltersCount =
		historicalFilterChips.length + (agregacao === 'mes' ? 1 : 0);
	const historicalPointCount = buildHistoricalChartPoints(
		serieData?.serie,
	).length;
	const historicalDaysCount =
		dataInicio && dataFim
			? Math.max(
					1,
					Math.floor(
						(new Date(`${dataFim}T00:00:00`) -
							new Date(`${dataInicio}T00:00:00`)) /
							86400000,
					) + 1,
				)
			: historicalPointCount;

	const handleExportSerieHistorica = async () => {
		try {
			setExportingSerie(true);
			const userCookie = Cookies.get('user');
			const user = userCookie ? JSON.parse(userCookie) : null;
			const token = user?.token;

			if (!token) {
				alert('Erro: Token não encontrado');
				return;
			}

			// Exporta dados do gráfico de série histórica usando endpoint /historico/export/
			const filters = {};
			if (dataInicio) filters.data_inicio = dataInicio;
			if (dataFim) filters.data_fim = dataFim;
			if (agregacao) filters.agregacao = agregacao;
			Object.entries(selectedHistoricalFilters).forEach(
				([filter, values]) => {
					if (values.length) filters[filter] = values;
				},
			);

			const response = await requestOcupationdeLeitosGraficoExcel(
				filters,
				token,
			);

			if (response && response instanceof Blob) {
				const url = window.URL.createObjectURL(response);
				const link = document.createElement('a');
				link.href = url;
				link.download = `serie_historica_ocupacao_${
					new Date().toISOString().split('T')[0]
				}.xlsx`;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				window.URL.revokeObjectURL(url);
			} else {
				alert('Erro ao exportar dados para Excel');
			}
		} catch (error) {
			console.error('Erro ao exportar série histórica:', error);
			alert('Erro ao exportar dados para Excel');
		} finally {
			setExportingSerie(false);
		}
	};

	const handleExportTabelaOcupacao = async () => {
		try {
			setExportingTabela(true);
			const userCookie = Cookies.get('user');
			const user = userCookie ? JSON.parse(userCookie) : null;
			const token = user?.token;

			if (!token) {
				alert('Erro: Token não encontrado');
				return;
			}

			// Exporta tabela de ocupação com todas as unidades/enfermarias conforme filtros
			const filters = getTableFilters();

			const response = await requestOcupationdeLeitosExcel(
				filters,
				token,
			);

			if (response && response instanceof Blob) {
				const url = window.URL.createObjectURL(response);
				const link = document.createElement('a');
				link.href = url;
				link.download = `ocupacao_leitos_${
					new Date().toISOString().split('T')[0]
				}.xlsx`;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				window.URL.revokeObjectURL(url);
			} else {
				alert('Erro ao exportar dados para Excel');
			}
		} catch (error) {
			console.error('Erro ao exportar tabela de ocupação:', error);
			alert('Erro ao exportar dados para Excel');
		} finally {
			setExportingTabela(false);
		}
	};

	useEffect(() => {
		if (!isAccessChecked || showAlert) return;

		// A tabela e o gráfico possuem fontes distintas e iniciam juntos após
		// a validação de acesso: /ocupacao-leitos/ e /ocupacao-leitos/historico/.
		loadFilterOptions();
		loadHistoricoFilterOptions();
		loadOcupacaoData({}, 1, itemsPerPage);
		loadSerieHistorica();
	}, [isAccessChecked, showAlert]);

	// Usar dados dos cards da API
	const cards = ocupacaoData.cards || {};

	const taxa_ocupacao_operacional = cards.taxa_ocupacao_operacional || 0;
	const taxa_ocupacao_hospitalar = cards.taxa_ocupacao_hospitalar || 0;
	const lastUpdate = ocupacaoData.ultima_atualizacao
		? new Date(ocupacaoData.ultima_atualizacao).toLocaleString('pt-BR')
		: '';

	// Lógica de paginação

	const registros = ocupacaoData.registros || [];
	const totalPages = ocupacaoData.total_paginas;

	const currentItems = registros;

	const handlePageChange = (pageNumber) => {
		setCurrentPage(pageNumber);

		// Reconstruir filtros atuais
		const filters = getTableFilters();

		loadOcupacaoData(filters, pageNumber, itemsPerPage);
	};

	const handleItemsPerPageChange = (value) => {
		setItemsPerPage(value);
		setCurrentPage(1); // Resetar para primeira página

		// Reconstruir filtros atuais
		const filters = getTableFilters();

		loadOcupacaoData(filters, 1, value);
	};

	// Configuração do gráfico ECharts com dados dinâmicos
	const getChartOption = () => {
		// A API define os períodos; consolidamos somente as linhas repetidas de
		// setores/unidades no mesmo período para a série única do gráfico.
		const dadosDaSerie = buildHistoricalChartPoints(serieData?.serie);
		const periodos = dadosDaSerie.map((item) =>
			String(item?.periodo_label ?? item?.periodo ?? ''),
		);

		const taxasHospitalares = dadosDaSerie.map((item) => {
			const taxa = Number(item.taxa_ocupacao_hospitalar);
			return Number.isFinite(taxa)
				? Math.round((taxa + Number.EPSILON) * 10) / 10
				: null;
		});

		// Detectar tamanho da tela para ajustes responsivos
		const isTablet = !isMobile && window.innerWidth < 1024;
		const isDesktop = window.innerWidth >= 1024;

		return {
			title: {
				text: 'Série Histórica - Ocupação de Leitos',
				left: 'center',
				top: isMobile ? 30 : 10,
				textStyle: {
					fontSize: 16,
					fontWeight: 'normal',
					color: '#13335A',
				},
			},
			tooltip: {
				trigger: 'axis',
				axisPointer: {
					type: 'cross',
					label: {
						backgroundColor: '#6a7985',
					},
				},
				formatter: function (params) {
					let result = `<strong>${params[0].axisValue}</strong><br/>`;
					params.forEach((item) => {
						result += `${item.marker} ${item.seriesName}: <strong>${formatTaxaOcupacao(item.value)}%</strong><br/>`;
					});
					return result;
				},
				textStyle: {
					fontSize: isMobile ? 10 : 12,
				},
			},
			legend: {
				show: false,
				data: ['Taxa Ocupação Hospitalar'],
				top: isMobile ? 25 : isTablet ? 30 : 35,
				textStyle: {
					fontSize: isMobile ? 9 : isTablet ? 10 : 12,
				},
				itemWidth: isMobile ? 20 : 25,
				itemHeight: isMobile ? 10 : 14,
			},

			toolbox: {
				feature: {
					saveAsImage: {
						title: 'Salvar como imagem',
					},
				},
				right: 30,
				top: isMobile ? 0 : 10,
			},
			grid: {
				left: '3%',
				right: '4%',
				bottom: '10%',
				containLabel: true,
			},
			xAxis: [
				{
					type: 'category',
					boundaryGap: false,
					data: periodos,
					axisLabel: {
						rotate: periodos.length > 15 ? 45 : 0,
						interval: periodos.length > 30 ? 'auto' : 0,
						fontSize: 11,
					},
				},
			],

			yAxis: [
				{
					type: 'value',
					name: 'Taxa (%)',
					nameTextStyle: {
						fontSize: isMobile ? 9 : isTablet ? 10 : 12,
						padding: isMobile ? [0, 0, 0, 0] : [0, 0, 0, 10],
					},
					axisLabel: {
						formatter: (value) =>
							`${formatTaxaOcupacao(value)}%`,
						fontSize: isMobile ? 8 : isTablet ? 9 : 11,
					},
					splitLine: {
						lineStyle: {
							width: isMobile ? 0.5 : 1,
						},
					},
				},
			],

			series: [
				{
					name: 'Taxa Ocupação Hospitalar',
					type: 'line',
					areaStyle: {
						opacity: isMobile ? 0.5 : 0.6,
					},
					lineStyle: {
						color: '#285cb0',
						width: isMobile ? 1.5 : isTablet ? 1.8 : 2,
					},
					itemStyle: {
						color: '#285cb0',
					},
					// Mantém a leitura do percentual disponível sem depender do hover.
					// Ao destacar o ponto, o tooltip passa a ser a referência detalhada.
					label: {
						show: true,
						position: 'top',
						color: '#13335A',
						fontSize: isMobile ? 8 : 10,
						fontWeight: 600,
						formatter: ({ value }) =>
							Number.isFinite(Number(value))
								? `${formatTaxaOcupacao(value)}%`
								: '',
					},
					emphasis: {
						focus: 'series',
						label: {
							show: false,
						},
					},
					smooth: isMobile ? 0.2 : 0,
					data: taxasHospitalares,
					symbolSize: isMobile ? 4 : 6,
				},
			],
		};
	};

	return (
		<div className='flex flex-col min-h-screen font-inter'>
			<NavBar />

			{showAlert ? (
				<div className='flex flex-col min-h-screen bg-gray-50'>
					<section className='bg-[#13335A] rounded-md w-[90%] sm:w-[70%] md:w-[60%] lg:w-[50%] mt-[130px] mx-auto my-auto'>
						<div className='py-8 px-4 mx-auto max-w-screen-xl lg:py-16 lg:px-6'>
							<div className='mx-auto max-w-screen-sm text-center'>
								<h1 className='mb-4 text-white text-5xl sm:text-6xl lg:text-7xl tracking-tight font-extrabold text-primary-600'>
									401
								</h1>
								<p className='mb-10 text-2xl sm:text-3xl md:text-4xl tracking-tight font-bold text-slate-100'>
									Acesso Negado
								</p>
								<p className='mb-7 text-sm sm:text-base md:text-lg font-light text-gray-300'>
									Você não possui permissão para acessar este
									campo. Entre em contato com o
									administrador(a) da sua unidade para
									solicitação de acesso.
								</p>
								<a
									href='/login'
									className='inline-flex hover:scale-105 transition-transform text-gray-300 bg-primary-600 hover:bg-primary-800 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg text-xl px-5 py-2.5 text-center my-4'>
									<span> Volte para a Tela de Login</span>
								</a>
							</div>
						</div>
					</section>
				</div>
			) : (
				<div className='flex flex-col min-h-screen bg-[#FFFFFF]'>
					{/* Header */}
					<div className='bg-[#F0F4F8] mt-[80px] h-[80px] sm:h-[110px] w-full'>
						<div className='w-full flex justify-center h-full align-middle items-center text-[#13335A] font-bold text-[20px] sm:text-[24px] md:text-[32px] font-inter p-4 sm:p-6 md:p-0'>
							Ocupação de Leitos
						</div>
					</div>

					{/* Tabs */}
					{/* <div className='bg-white border-b border-gray-200'>
						<div className='max-w-7xl mx-auto px-2 sm:px-4'>
							<div className='flex justify-center sm:space-x-8 space-x-2 overflow-x-auto'>
								<button
									onClick={() => setActiveTab('ocupacao')}
									className={`py-2 px-2 sm:px-2 font-medium text-[8px] sm:text-xs md:text-sm transition-colors whitespace-nowrap ${
										activeTab === 'ocupacao'
											? 'text-[#42B9EB] border-b-2 border-[#42B9EB]'
											: 'text-gray-500 hover:text-gray-700'
									}`}>
									Ocupação de leitos
								</button>
								<div className='border-r border-gray-300 h-6 self-center'></div>
								<button
									onClick={() => setActiveTab('indicadores')}
									className={`py-2 px-2 sm:px-4 font-medium text-[10px] sm:text-xs md:text-sm transition-colors whitespace-nowrap ${
										activeTab === 'indicadores'
											? 'text-[#42B9EB] border-b-2 border-[#42B9EB]'
											: 'text-gray-500 hover:text-gray-700'
									}`}>
									Indicadores
								</button>
								<div className='border-r border-gray-300 h-6 self-center'></div>
								<button
									onClick={() => setActiveTab('doencas')}
									className={`py-2 px-2 sm:px-2 font-medium text-[8px] sm:text-xs md:text-sm transition-colors whitespace-nowrap ${
										activeTab === 'doencas'
											? 'text-[#42B9EB] border-b-2 border-[#42B9EB]'
											: 'text-gray-500 hover:text-gray-700'
									}`}>
									Doenças e agravos
								</button>
							</div>
						</div>
					</div> */}

					{/* Content */}
					<div className='w-[90%] sm:w-[95%] mx-auto items-center max-w-[1136px] py-8 px-4 sm:px-0'>
						{activeTab === 'ocupacao' && (
							<div className='space-y-6'>
								{/* Subtitle */}
								<div className='w-full h-full align-middle items-center text-[#13335A] font-semibold text-[20px] md:text-[32px]'>
									Ocupação de leitos{' '}
									<p className='text-[#4F5257] font-inter text-[12px] md:text-[14px] font-normal'>
										Monitoramento da disponibilidade e
										ocupação de leitos na rede, com base das
										últimas informações disponíveis
									</p>
								</div>
								{/* 	{console.log(
								{/* Filters */}
								<div className='flex flex-wrap gap-4 items-end'>
									<SearchableMultiSelect
										label='Área Programática'
										options={filterOptions.areas_programaticas}
										selectedOptions={
											selectedVisualFilters.areaProgramatica
										}
										searchTerm={
											visualFilterSearch.areaProgramatica || ''
										}
										isOpen={openVisualFilter === 'areaProgramatica'}
										onOpen={() => setOpenVisualFilter('areaProgramatica')}
						onClose={() =>
							setOpenVisualFilter((current) =>
								current === 'areaProgramatica' ? null : current,
							)
						}
										onSearchChange={(value) =>
											setVisualFilterSearch((previous) => ({
												...previous,
												areaProgramatica: value,
											}))
										}
										onToggleOption={(option) =>
											toggleVisualFilterOption('areaProgramatica', option)
										}
										labelClassName='mb-2 block text-[16px] font-inter text-[#4F5257]'
									/>
									<SearchableMultiSelect
										label='Superintendência'
										options={availableSuperintendencias}
										selectedOptions={selectedVisualFilters.superintendencia}
										searchTerm={visualFilterSearch.superintendencia || ''}
										isOpen={openVisualFilter === 'superintendencia'}
										onOpen={() => setOpenVisualFilter('superintendencia')}
						onClose={() =>
							setOpenVisualFilter((current) =>
								current === 'superintendencia' ? null : current,
							)
						}
										onSearchChange={(value) =>
											setVisualFilterSearch((previous) => ({
												...previous,
												superintendencia: value,
											}))
										}
										onToggleOption={(option) =>
											toggleVisualFilterOption('superintendencia', option)
										}
										labelClassName='mb-2 block text-[16px] font-inter text-[#4F5257]'
									/>
									<SearchableMultiSelect
										label='Unidade'
										options={availableUnidades}
										selectedOptions={selectedVisualFilters.unidade}
										searchTerm={visualFilterSearch.unidade || ''}
										isOpen={openVisualFilter === 'unidade'}
										onOpen={() => setOpenVisualFilter('unidade')}
						onClose={() =>
							setOpenVisualFilter((current) =>
								current === 'unidade' ? null : current,
							)
						}
										onSearchChange={(value) =>
											setVisualFilterSearch((previous) => ({
												...previous,
												unidade: value,
											}))
										}
										onToggleOption={(option) =>
											toggleVisualFilterOption('unidade', option)
										}
										labelClassName='mb-2 block text-[16px] font-inter text-[#4F5257]'
									/>
									<SearchableMultiSelect
										label='Tipos'
										options={availableSeccoes}
										selectedOptions={selectedVisualFilters.tipo}
										searchTerm={visualFilterSearch.tipo || ''}
										isOpen={openVisualFilter === 'tipo'}
										onOpen={() => setOpenVisualFilter('tipo')}
						onClose={() =>
							setOpenVisualFilter((current) =>
								current === 'tipo' ? null : current,
							)
						}
										onSearchChange={(value) =>
											setVisualFilterSearch((previous) => ({
												...previous,
												tipo: value,
											}))
										}
										onToggleOption={(option) =>
											toggleVisualFilterOption('tipo', option)
										}
										labelClassName='mb-2 block text-[16px] font-inter text-[#4F5257]'
									/>
									<SearchableMultiSelect
										label='Enfermarias'
										options={availableEnfermarias}
										selectedOptions={selectedVisualFilters.enfermaria}
										searchTerm={visualFilterSearch.enfermaria || ''}
										isOpen={openVisualFilter === 'enfermaria'}
										onOpen={() => setOpenVisualFilter('enfermaria')}
						onClose={() =>
							setOpenVisualFilter((current) =>
								current === 'enfermaria' ? null : current,
							)
						}
										onSearchChange={(value) =>
											setVisualFilterSearch((previous) => ({
												...previous,
												enfermaria: value,
											}))
										}
										onToggleOption={(option) =>
											toggleVisualFilterOption('enfermaria', option)
										}
										labelClassName='mb-2 block text-[16px] font-inter text-[#4F5257]'
									/>

									<div className='flex gap-2'>
										<button
											type='button'
											onClick={handleApplyFilters}
											disabled={loading}
											className='px-6 py-2 bg-[#13335A] text-white rounded-md hover:bg-[#0d2440] transition-colors text-sm font-medium'>
											Aplicar Filtros
										</button>
										<button
											type='button'
											onClick={handleClearFilters}
											disabled={loading}
											className='px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm font-medium'>
											Limpar
										</button>
									</div>
								</div>

								<div className='hidden'>
								<div className='flex flex-wrap gap-4 items-end'>
									{/* Área Programática */}
									<div className='flex-1 min-w-[200px]'>
										<label className='block text-[16px] font-inter text-[#4F5257] mb-2'>
											Área Programática
										</label>
										<select
											value={selectedAreaProgramatica}
											onChange={(e) => {
												const value = e.target.value;
												setSelectedAreaProgramatica(
													value,
												);
												setSelectedSuperintendencia('');
												setSelectedUnidade('');
												setSelectedTipo('');
												setSelectedEnfermaria('');
												if (value !== '') {
													loadFilterOptions(value);
												} else {
													setAvailableSuperintendencias(
														filterOptions.superintendencias,
													);
													setAvailableUnidades(
														filterOptions.unidades,
													);
													setAvailableSeccoes(
														filterOptions.tipos,
													);
													setAvailableEnfermarias(
														filterOptions.setores,
													);
												}
											}}
											className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm ${
												!selectedAreaProgramatica
													? 'text-[#E3E3E3]'
													: ''
											}`}>
											<option value=''>Selecione</option>
											{filterOptions.areas_programaticas.map(
												(ap, index) => (
													<option
														key={index}
														value={ap}
														className='text-black'>
														{ap}
													</option>
												),
											)}
										</select>
									</div>

									{/* Superintendência */}
									<div className='flex-1 min-w-[200px]'>
										<label className='block text-[16px] font-inter text-[#4F5257] mb-2'>
											Superintendência
										</label>
										<select
											value={selectedSuperintendencia}
											onChange={(e) => {
												const value = e.target.value;
												setSelectedSuperintendencia(
													value,
												);
												setSelectedUnidade('');
												setSelectedTipo('');
												setSelectedEnfermaria('');
												if (value !== '') {
													loadFilterOptions(
														selectedAreaProgramatica,
														value,
													);
												} else {
													setAvailableUnidades(
														filterOptions.unidades,
													);
													setAvailableSeccoes(
														filterOptions.tipos,
													);
													setAvailableEnfermarias(
														filterOptions.setores,
													);
												}
											}}
											className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm ${
												!selectedSuperintendencia
													? 'text-[#E3E3E3]'
													: ''
											}`}>
											<option value=''>Selecione</option>
											{availableSuperintendencias.map(
												(s, index) => (
													<option
														key={index}
														value={s}
														className='text-black'>
														{s}
													</option>
												),
											)}
										</select>
									</div>
									{/* 		{console.log(filterOptions)} */}
									{/* 	{console.log('opções dos filtros', {
										superintendencias:
											availableSuperintendencias,
										unidades: availableUnidades,
										tipos: availableSeccoes,
										enfermarias: availableEnfermarias,
									})} */}
									{/* Unidade */}
									<div className='flex-1 min-w-[200px]'>
										<label className='block text-[16px] font-inter text-[#4F5257] mb-2'>
											Unidade
										</label>
										<select
											value={selectedUnidade}
											onChange={(e) => {
												const value = e.target.value;
												setSelectedUnidade(value);
												setSelectedTipo('');
												setSelectedEnfermaria('');
												if (value !== '') {
													loadFilterOptions(
														selectedAreaProgramatica,
														selectedSuperintendencia,
														value,
													);
												} else {
													setAvailableSeccoes(
														filterOptions.tipos,
													);
													setAvailableEnfermarias(
														filterOptions.setores,
													);
												}
											}}
											className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm ${
												!selectedUnidade
													? 'text-[#E3E3E3]'
													: ''
											}`}>
											<option value=''>Selecione</option>
											{availableUnidades.map(
												(unidade, index) => (
													<option
														key={index}
														value={unidade}
														className='text-black'>
														{unidade}
													</option>
												),
											)}
										</select>
									</div>

									{/* Tipos */}
									<div className='flex-1 min-w-[200px]'>
										<label className='block text-[16px] font-inter text-[#4F5257] mb-2'>
											Tipos
										</label>
										<select
											value={selectedTipo}
											onChange={(e) => {
												const value = e.target.value;
												setSelectedTipo(value);
												setSelectedEnfermaria('');
												if (value !== '') {
													loadFilterOptions(
														selectedAreaProgramatica,
														selectedSuperintendencia,
														selectedUnidade,
														value,
													);
												} else {
													setAvailableEnfermarias(
														filterOptions.setores,
													);
												}
											}}
											className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm ${
												!selectedTipo
													? 'text-[#E3E3E3]'
													: ''
											}`}>
											<option value=''>Selecione</option>
											{availableSeccoes.map(
												(tipo, index) => (
													<option
														key={index}
														value={tipo}
														className='text-black'>
														{tipo}
													</option>
												),
											)}
										</select>
									</div>

									{/* Enfermarias */}
									<div className='flex-1 min-w-[200px]'>
										<label className='block text-[16px] font-inter text-[#4F5257] mb-2'>
											Enfermarias
										</label>
										<select
											value={selectedEnfermaria}
											onChange={(e) =>
												setSelectedEnfermaria(
													e.target.value,
												)
											}
											className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm ${
												!selectedEnfermaria
													? 'text-[#E3E3E3]'
													: ''
											}`}>
											<option value=''>Selecione</option>
											{availableEnfermarias.map(
												(enf, index) => (
													<option
														key={index}
														value={enf}
														className='text-black'>
														{enf}
													</option>
												),
											)}
										</select>
									</div>

									{/* Botões */}
									<div className='flex gap-2'>
										<button
											onClick={handleApplyFilters}
											disabled={loading}
											className='px-6 py-2 bg-[#13335A] text-white rounded-md hover:bg-[#0d2440] transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed'>
											{loading
												? 'Carregando...'
												: 'Aplicar Filtros'}
										</button>
										<button
											onClick={handleClearFilters}
											disabled={loading}
											className='px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed'>
											Limpar
										</button>
									</div>
								</div>
								</div>

								{/* Panorama Atual */}
								<div className='bg-white p-4 sm:p-6 flex flex-col rounded-lg shadow-sm border border-gray-200 gap-[12px]'>
									<div className='flex justify-between'>
										<h3 className='text-[14px] sm:text-[16px] md:text-[18px] font-semibold text-[#4F5257] '>
											Panorama Atual
										</h3>
										<div className='text-right -mt-1'>
											<p className='text-xs text-gray-500'>
												{lastUpdate
													? `Última atualização: ${lastUpdate}`
													: 'Carregando...'}
											</p>
										</div>
									</div>
									<div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4'>
										{/* Stats Cards */}

										{/* Leitos Operacionais */}
										<div className='bg-white p-2 sm:p-4 rounded-lg border-l-4 border-l-[#3A5786] border-t border-r border-b border-[#E5E5E5] shadow-sm'>
											<div className='flex gap-2'>
												<p className='text-[10px] sm:text-[12px] md:text-[14px] text-gray-600 mb-1 sm:mb-2'>
													Leitos Operacionais
												</p>
												<details
													className='relative inline-block'
													open={
														openTooltip ===
														'leitos_operacionais'
													}
													onClick={(e) => {
														e.preventDefault();
														setOpenTooltip(
															(prev) =>
																prev ===
																'leitos_operacionais'
																	? null
																	: 'leitos_operacionais',
														);
													}}>
													<summary className='list-none cursor-pointer text-gray-400 text-sm font-semibold'>
														ⓘ
													</summary>

													<div className='absolute z-50 mt-2 w-64 p-3 text-sm text-white bg-gray-800 rounded-lg shadow-lg'>
														{
															cards?.tooltips
																?.leitos_operacionais
														}
													</div>
												</details>
											</div>

											<p className='text-[16px] sm:text-[18px] md:text-[22px] font-bold text-[#4F5257]'>
												{cards?.leitos_operacionais ||
													0}
											</p>
										</div>

										{/* Leitos Ocupados */}
										<div className='bg-white p-2 sm:p-4 rounded-lg border-l-4 border-l-[#42B9EB] border-t border-r border-b border-[#E5E5E5] shadow-sm'>
											<div className='flex gap-2'>
												<p className='text-[10px] sm:text-[12px] md:text-[14px] text-gray-600 mb-1 sm:mb-2'>
													Leitos Ocupados
												</p>
												<details
													className='relative inline-block'
													open={
														openTooltip ===
														'total_de_pacientes'
													}
													onClick={(e) => {
														e.preventDefault();
														setOpenTooltip(
															(prev) =>
																prev ===
																'total_de_pacientes'
																	? null
																	: 'total_de_pacientes',
														);
													}}>
													<summary className='list-none cursor-pointer text-gray-400 text-sm font-semibold'>
														ⓘ
													</summary>

													<div className='absolute z-50 mt-2 w-64 p-3 text-sm text-white bg-gray-800 rounded-lg shadow-lg'>
														{
															cards?.tooltips
																?.total_de_pacientes
														}
													</div>
												</details>
											</div>
											<p className='text-[16px] sm:text-[18px] md:text-[22px] font-bold text-[#4F5257]'>
												{cards?.total_de_pacientes || 0}
											</p>
										</div>

										{/* Taxa de Ocupação */}
										<div className='bg-white p-2 sm:p-4 rounded-lg border-l-4 border-l-[#A855F7] border-t border-r border-b border-[#E5E5E5] shadow-sm'>
											<div className='flex gap-2'>
												<p className='text-[10px] sm:text-[12px] md:text-[14px] text-gray-600 mb-1 sm:mb-2'>
													Taxa de Ocupação
												</p>
												<details
													className='relative inline-block'
													open={openTooltip === 'taxa_ocupacao_hospitalar'}
													onClick={(e) => {
														e.preventDefault();
														setOpenTooltip((previous) =>
															previous === 'taxa_ocupacao_hospitalar'
																? null
																: 'taxa_ocupacao_hospitalar',
														);
													}}>
													<summary className='list-none cursor-pointer text-gray-400 text-sm font-semibold'>
														ⓘ
													</summary>
													<div className='absolute z-50 mt-2 w-64 p-3 text-sm text-white bg-gray-800 rounded-lg shadow-lg'>
														{cards?.tooltips?.taxa_ocupacao_hospitalar}
													</div>
												</details>
											</div>
											<p className='text-[16px] sm:text-[18px] md:text-[22px] font-bold text-[#4F5257]'>
												{formatTaxaOcupacao(
													taxa_ocupacao_hospitalar,
												)}%
											</p>
										</div>

										{/* macas ocupadas */}
										<div className='bg-white p-2 sm:p-4 rounded-lg border-l-4 border-l-[#868686] border-t border-r border-b border-[#E5E5E5] shadow-sm'>
											<div className='flex gap-2'>
												<p className='text-[10px] sm:text-[12px] md:text-[14px] text-gray-600 mb-1 sm:mb-2'>
													Macas Ocupadas
												</p>
												<details
													className='relative inline-block'
													open={
														openTooltip ===
														'pacientes_em_maca'
													}
													onClick={(e) => {
														e.preventDefault();
														setOpenTooltip(
															(prev) =>
																prev ===
																'pacientes_em_maca'
																	? null
																	: 'pacientes_em_maca',
														);
													}}>
													<summary className='list-none cursor-pointer text-gray-400 text-sm font-semibold'>
														ⓘ
													</summary>

													<div className='absolute z-50 mt-2 w-64 p-3 text-sm text-white bg-gray-800 rounded-lg shadow-lg'>
														{
															cards?.tooltips
																?.pacientes_em_maca
														}
													</div>
												</details>
											</div>
											<p className='text-[16px] sm:text-[18px] md:text-[22px] font-bold text-[#4F5257]'>
												{cards?.pacientes_em_maca || 0}
											</p>
										</div>

										{/* Leitos Livres */}
										<div className='bg-white p-2 sm:p-4 rounded-lg border-l-4 border-l-[#F97316] border-t border-r border-b border-[#E5E5E5] shadow-sm'>
											<div className='flex gap-2'>
												<p className='text-[10px] sm:text-[12px] md:text-[14px] text-gray-600 mb-1 sm:mb-2'>
													Leitos Livres
												</p>
												<details
													className='relative inline-block'
													open={
														openTooltip ===
														'leitos_livres'
													}
													onClick={(e) => {
														e.preventDefault();
														setOpenTooltip(
															(prev) =>
																prev ===
																'leitos_livres'
																	? null
																	: 'leitos_livres',
														);
													}}>
													<summary className='list-none cursor-pointer text-gray-400 text-sm font-semibold'>
														ⓘ
													</summary>

													<div className='absolute z-50 mt-2 w-64 p-3 text-sm text-white bg-gray-800 rounded-lg shadow-lg'>
														{
															cards?.tooltips
																?.leitos_livres
														}
													</div>
												</details>
											</div>
											<p className='text-[16px] sm:text-[18px] md:text-[22px] font-bold text-[#4F5257]'>
												{cards.leitos_livres || 0}
											</p>
										</div>

										{/* Leitos em Manutenção */}
										<div className='bg-white p-2 sm:p-4 rounded-lg border-l-4 border-l-[#32993E] border-t border-r border-b border-[#E5E5E5] shadow-sm'>
											<div className='flex justify-between'>
												<p className='text-[10px] sm:text-[12px] md:text-[14px] text-gray-600 mb-1 sm:mb-2'>
													Leitos em Manutenção
												</p>
												<details
													className='relative inline-block'
													open={
														openTooltip ===
														'leitos_bloqueados'
													}
													onClick={(e) => {
														e.preventDefault();
														setOpenTooltip(
															(prev) =>
																prev ===
																'leitos_bloqueados'
																	? null
																	: 'leitos_bloqueados',
														);
													}}>
													<summary className='list-none cursor-pointer text-gray-400 text-sm font-semibold'>
														ⓘ
													</summary>

													<div className='absolute z-50 mt-2 w-64 p-3 text-sm text-white bg-gray-800 rounded-lg shadow-lg'>
														{
															cards?.tooltips
																?.leitos_bloqueados
														}
													</div>
												</details>
											</div>
											<p className='text-[16px] sm:text-[18px] md:text-[22px] font-bold text-[#4F5257]'>
												{cards.leitos_bloqueados || 0}
											</p>
										</div>
									</div>

									{/* Occupation Rates */}
									<div className='hidden grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-4 '>
										{/* Taxa de Ocupação Hospitalar */}
										<div className='bg-white p-2 sm:p-4 rounded-lg border-l-4 border-l-[#A855F7] border-t border-r border-b border-[#E5E5E5] shadow-sm'>
											<div className='flex gap-2'>
												<p className='text-[10px] sm:text-[12px] md:text-[14px] text-gray-600 mb-1 sm:mb-2'>
													Taxa de Ocupação Hospitalar
												</p>

												<details
													className='relative inline-block'
													open={
														openTooltip ===
														'taxa_ocupacao_hospitalar'
													}
													onClick={(e) => {
														e.preventDefault();
														setOpenTooltip(
															(prev) =>
																prev ===
																'taxa_ocupacao_hospitalar'
																	? null
																	: 'taxa_ocupacao_hospitalar',
														);
													}}>
													<summary className='list-none cursor-pointer text-gray-400 text-sm font-semibold'>
														ⓘ
													</summary>

													<div className='absolute z-50 mt-2 w-64 p-3 text-sm text-white bg-gray-800 rounded-lg shadow-lg'>
														{
															cards?.tooltips
																?.taxa_ocupacao_hospitalar
														}
													</div>
												</details>
											</div>
											<p className='text-[16px] sm:text-[18px] md:text-[22px] font-bold text-[#4F5257]'>
												{formatTaxaOcupacao(
													taxa_ocupacao_hospitalar,
												)}%
											</p>
										</div>
										{/* Taxa de Ocupação Total */}
										<div className='bg-white p-2 sm:p-4 rounded-lg border-l-4 border-l-[#EAB308] border-t border-r border-b border-[#E5E5E5] shadow-sm'>
											<div className='flex gap-2'>
												<p className='text-[10px] sm:text-[12px] md:text-[14px] text-gray-600 mb-1 sm:mb-2'>
													Taxa de Ocupação Operacional
												</p>
												<details
													className='relative inline-block'
													open={
														openTooltip ===
														'taxa_ocupacao_operacional'
													}
													onClick={(e) => {
														e.preventDefault();
														setOpenTooltip(
															(prev) =>
																prev ===
																'taxa_ocupacao_operacional'
																	? null
																	: 'taxa_ocupacao_operacional',
														);
													}}>
													<summary className='list-none cursor-pointer text-gray-400 text-sm font-semibold'>
														ⓘ
													</summary>

													<div className='absolute z-50 mt-2 w-64 p-3 text-sm text-white bg-gray-800 rounded-lg shadow-lg'>
														{
															cards?.tooltips
																?.taxa_ocupacao_operacional
														}
													</div>
												</details>
											</div>
											<p className='text-[16px] sm:text-[18px] md:text-[22px] font-bold text-[#4F5257]'>
												{formatTaxaOcupacao(
													taxa_ocupacao_operacional,
												)}%
											</p>
										</div>
									</div>

									{/* Tabela de Ocupação por Unidade - Enfermaria */}
									<div className='mt-6 flex flex-col'>
										<div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3'>
											<h3 className='text-[14px] sm:text-[16px] md:text-[18px] font-semibold text-[#4F5257]'>
												Ocupação por Unidade -
												Enfermaria
											</h3>
											<button
												onClick={
													handleExportTabelaOcupacao
												}
												disabled={exportingTabela}
												className='w-full sm:w-auto p-2 sm:p-[12px] border border-gray-300 rounded-md text-xs sm:text-sm text-[#757575] flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-[#195a36] hover:text-white hover:border-[#195a36] disabled:cursor-not-allowed transition-all duration-300 shadow-[0_2px_2px_rgba(0,0,0,0.2)]'>
												{exportingTabela ? (
													<>⏳ Exportando...</>
												) : (
													<>
														<AiOutlineFileExcel className='w-[16px] sm:w-[20px] h-auto' />
														<span className='hidden sm:inline'>
															Exportar em Excel
														</span>
														<span className='sm:hidden'>
															Excel
														</span>
													</>
												)}
											</button>
										</div>
										<hr className='mt-[12px] '></hr>
									</div>

									{/* Tabela */}
									<div className='overflow-x-auto sm:mx-0 border rounded-lg'>
										<table className='w-full border-collapse min-w-[800px]'>
											<thead>
												<tr className='bg-[#13335A] text-white'>
													<th className='px-2 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs md:text-sm font-semibold first:rounded-tl-[8px]'>
														Unidade
														<br />
														Enfermaria
													</th>
													<th className='px-2 sm:px-4 py-2 sm:py-3 text-center text-[10px] sm:text-xs md:text-sm font-semibold'>
														Leitos Operacionais
													</th>
													<th className='px-2 sm:px-4 py-2 sm:py-3 text-center text-[10px] sm:text-xs md:text-sm font-semibold'>
														Leitos Ocupados
													</th>
													<th className='px-2 sm:px-4 py-2 sm:py-3 text-center text-[10px] sm:text-xs md:text-sm font-semibold'>
														Macas Ocupadas
													</th>
													<th className='px-2 sm:px-4 py-2 sm:py-3 text-center text-[10px] sm:text-xs md:text-sm font-semibold'>
														Leitos Livres
													</th>

													<th className='px-2 sm:px-4 py-2 sm:py-3 text-center text-[10px] sm:text-xs md:text-sm font-semibold'>
														Leitos em Manutenção
													</th>

													<th className='px-2 sm:px-4 py-2 sm:py-3 text-center text-[10px] sm:text-xs md:text-sm font-semibold'>
														Taxa
														<br className='hidden sm:block' />
														<span className='hidden sm:inline'>
															ocupação{' '}
														</span>
														oper.
													</th>
													<th className='px-2 sm:px-4 py-2 sm:py-3 text-center text-[10px] sm:text-xs md:text-sm font-semibold last:rounded-tr-[8px]'>
														Taxa
														<br className='hidden sm:block' />
														<span className='hidden sm:inline'>
															ocupação{' '}
														</span>
														hosp.
													</th>
												</tr>
											</thead>

											<tbody className='bg-white '>
												{currentItems.length > 0 ? (
													currentItems.map(
														(registro, index) => (
															<tr
																key={index}
																className={`hover:bg-gray-50 ${
																	index ===
																	currentItems.length -
																		1
																		? 'border-b-0'
																		: ''
																}  ${
																	index %
																		2 ===
																	1
																		? 'bg-[#F9F9F9] border border-l-0 border-r-0'
																		: ''
																}`}>
																<td
																	className={`px-2 sm:px-4 py-2 sm:py-3 text-[10px] sm:text-xs md:text-sm text-gray-700 ${
																		index ===
																		currentItems.length -
																			1
																			? 'rounded-bl-[8px]'
																			: ''
																	}`}>
																	<div className='font-medium'>
																		{registro?.estabelecimento_nome ||
																			'-'}
																	</div>
																	<div className='text-gray-500 text-[9px] sm:text-[10px] md:text-xs'>
																		{registro?.secao_nome ||
																			'-'}
																	</div>
																</td>
																<td className='px-2 sm:px-4 py-2 sm:py-3 text-[10px] sm:text-xs md:text-sm text-center text-gray-700 '>
																	{registro?.leitos_operacionais ||
																		0}
																</td>
																<td className='px-2 sm:px-4 py-2 sm:py-3 text-[10px] sm:text-xs md:text-sm text-center text-gray-700 '>
																	{registro?.total_de_pacientes ||
																		0}
																</td>
																<td className='px-2 sm:px-4 py-2 sm:py-3 text-[10px] sm:text-xs md:text-sm text-center text-gray-700 '>
																	{registro?.pacientes_em_maca ||
																		0}
																</td>
																<td className='px-2 sm:px-4 py-2 sm:py-3 text-[10px] sm:text-xs md:text-sm text-center text-gray-700 '>
																	{registro?.leitos_livres ||
																		0}
																</td>
																<td className='px-2 sm:px-4 py-2 sm:py-3 text-[10px] sm:text-xs md:text-sm text-center text-gray-700 '>
																	{registro?.leitos_bloqueados ||
																		0}
																</td>

																<td className='px-2 sm:px-4 py-2 sm:py-3 text-[10px] sm:text-xs md:text-sm text-center '>
																	<span
																		className={`inline-block min-w-[60px] sm:min-w-[80px] px-2 sm:px-3 py-1 rounded-md text-[9px] sm:text-[10px] md:text-xs font-medium ${
																			(registro?.taxa_ocupacao_operacional ??
																				0) >=
																			85
																				? 'bg-red-100 text-red-700 border border-red-300'
																				: (registro?.taxa_ocupacao_operacional ??
																							0) >=
																					  60
																					? 'bg-green-100 text-green-700 border border-green-300'
																					: 'bg-green-100 text-green-700 border border-green-300'
																		}`}>
																		{registro?.taxa_ocupacao_operacional !=
																		null
													? `${formatTaxaOcupacao(
															registro?.taxa_ocupacao_operacional,
														)}%`
																			: '--'}
																	</span>
																</td>
																<td className='px-2 sm:px-4 py-2 sm:py-3 text-[10px] sm:text-xs md:text-sm text-center '>
																	<span
																		className={`inline-block min-w-[60px] sm:min-w-[80px] px-2 sm:px-3 py-1 rounded-md text-[9px] sm:text-[10px] md:text-xs font-medium ${
																			(registro?.taxa_ocupacao_hospitalar ??
																				0) >=
																			85
																				? 'bg-red-100 text-red-700 border border-red-300'
																				: (registro?.taxa_ocupacao_hospitalar ??
																							0) >=
																					  60
																					? 'bg-green-100 text-green-700 border border-green-300'
																					: 'bg-green-100 text-green-700 border border-green-300'
																		}`}>
																		{registro?.taxa_ocupacao_hospitalar !=
																		null
													? `${formatTaxaOcupacao(
															registro?.taxa_ocupacao_hospitalar,
														)}%`
																			: '--'}
																	</span>
																</td>
															</tr>
														),
													)
												) : (
													<tr>
														<td
															colSpan='8'
															className='px-4 py-8 text-center text-gray-500 text-xs sm:text-sm'>
															Nenhum registro
															encontrado
														</td>
													</tr>
												)}
											</tbody>
										</table>
									</div>

									{/* Paginação */}
									{registros.length > 0 && (
										<div className='flex flex-col sm:flex-row items-center gap-3 sm:gap-0 justify-between mt-4 px-0 sm:px-4'>
											<div className='flex items-center gap-2 text-xs sm:text-sm'>
												<span className='text-gray-700'>
													Exibindo
												</span>
												<SearchablePageSizeSelect
													value={itemsPerPage}
													onChange={handleItemsPerPageChange}
												/>
												<span className='text-gray-700'>
													de {registros.length}{' '}
													<span className='hidden sm:inline'>
														Enfermarias
													</span>
												</span>
											</div>

											<div className='flex items-center gap-1 sm:gap-2'>
												<button
													onClick={() =>
														handlePageChange(
															currentPage - 1,
														)
													}
													disabled={currentPage === 1}
													className='px-2 sm:px-3 py-1 text-[10px] sm:text-xs md:text-sm text-[#4F5257] hover:text-[#6e7175] disabled:opacity-50 disabled:cursor-not-allowed flex items-center content-center gap-2'>
													<FaArrowLeft />
													<span className='hidden sm:inline'>
														Anterior
													</span>

													<span className='sm:hidden'>
														Anterior
													</span>
												</button>

												<div className='flex gap-1'>
													{currentPage > 2 && (
														<>
															<button
																onClick={() =>
																	handlePageChange(
																		1,
																	)
																}
																className='px-2 sm:px-3 py-1 rounded-md text-[10px] sm:text-xs md:text-sm text-gray-700 hover:bg-gray-50'>
																1
															</button>
															{currentPage >
																3 && (
																<span className='px-1 sm:px-2 py-1 text-gray-500 text-[10px] sm:text-xs'>
																	...
																</span>
															)}
														</>
													)}

													{Array.from(
														{ length: totalPages },
														(_, i) => i + 1,
													)
														.filter(
															(page) =>
																page ===
																	currentPage ||
																page ===
																	currentPage -
																		1 ||
																page ===
																	currentPage +
																		1,
														)
														.map((page) => (
															<button
																key={page}
																onClick={() =>
																	handlePageChange(
																		page,
																	)
																}
																className={`px-2 sm:px-3 py-1.5  rounded-md text-[10px] sm:text-xs md:text-sm ${
																	currentPage ===
																	page
																		? 'bg-[#13335A] text-white border-[#13335A]'
																		: 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-gray-500'
																}`}>
																{page}
															</button>
														))}

													{currentPage <
														totalPages - 1 && (
														<>
															{currentPage <
																totalPages -
																	2 && (
																<span className='px-1 sm:px-2 py-1 text-gray-500 text-[10px] sm:text-xs'>
																	...
																</span>
															)}
															<button
																onClick={() =>
																	handlePageChange(
																		totalPages,
																	)
																}
																className='px-2 sm:px-3 py-1  rounded-md text-[10px] sm:text-xs md:text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-500'>
																{totalPages}
															</button>
														</>
													)}
												</div>

												<button
													onClick={() =>
														handlePageChange(
															currentPage + 1,
														)
													}
													disabled={
														currentPage ===
														totalPages
													}
													className='px-2 sm:px-3 py-1  rounded-md text-[10px] sm:text-xs md:text-sm text-[#4F5257] hover:text-[#6e7175] disabled:opacity-50 disabled:cursor-not-allowed flex items-center content-center gap-2'>
													<span className='hidden sm:inline'>
														Próxima
													</span>
													<FaArrowRight />
													<span className='sm:hidden'>
														Prox
													</span>
														</button>
													</div>
												</div>
											)}
								</div>
								{/* Série Histórica */}
								<div className='bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200'>
									<div className='mb-4'>
										<div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0'>
											<div>
												<h3 className='text-[16px] sm:text-[19px] font-semibold text-[#4F5257] flex items-center gap-2'>
													Série histórica
													<details
														className='relative inline-block'
														open={
															openTooltip ===
															'serie_data'
														}
														onClick={(e) => {
															e.preventDefault();
															setOpenTooltip(
																(prev) =>
																	prev ===
																	'serie_data'
																		? null
																		: 'serie_data',
															);
														}}>
														<summary className='list-none cursor-pointer text-gray-400 text-sm font-semibold'>
															ⓘ
														</summary>

														<div className='absolute z-50 mt-2 w-64 p-3 text-sm text-white bg-gray-800 rounded-lg shadow-lg'>
															{serieData?.tooltip}
														</div>
													</details>
												</h3>
												<p className='text-[12px] sm:text-[14px] md:text-[17px] text-gray-500 mt-1'>
													Taxa de ocupação hospitalar
												</p>
											</div>

											<button
												onClick={
													handleExportSerieHistorica
												}
												disabled={exportingSerie}
												className='w-full sm:w-auto p-2 sm:p-[12px] border border-gray-300 rounded-md text-xs sm:text-sm text-[#757575] flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-[#195a36] hover:text-white hover:border-[#195a36] disabled:cursor-not-allowed shadow-[0_2px_2px_rgba(0,0,0,0.2)] transition-all duration-300'>
												{exportingSerie ? (
													<>⏳ Exportando...</>
												) : (
													<>
														<AiOutlineFileExcel className='w-[16px] sm:w-[20px] h-auto' />
														Exportar em Excel
													</>
												)}
											</button>
										</div>
										<div className='border-b mt-[12px] border-gray-300 '></div>
									</div>

									{/* Filtros de Data */}
					<div className='mb-6 overflow-visible rounded-lg border border-[#E4E9F0] bg-white'>
										<div className='flex flex-wrap items-center gap-3 rounded-lg bg-[#F7FAFE] px-4 py-3'>
											<div className='flex items-center gap-2 text-sm font-semibold text-[#13335A]'>
												<FiSliders className='h-4 w-4' aria-hidden='true' />
												Filtros do gráfico
												{!isHistoricalFiltersOpen && (
													<span className='inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-[#13335A] p-0 text-center text-xs leading-none text-white'>
														{activeHistoricalFiltersCount}
													</span>
												)}
											</div>
											{isHistoricalFiltersOpen && (
												<span className='h-5 w-px bg-[#DFE6EE]'></span>
											)}
											<button
												type='button'
												onClick={() => setIsHistoricalFiltersOpen((previous) => !previous)}
												className='order-10 ml-auto inline-flex items-center gap-1 rounded-full border border-[#C9D9EC] bg-white px-3 py-1.5 text-xs font-medium text-[#13335A] hover:bg-[#F7FAFE]'>
												{isHistoricalFiltersOpen ? 'Recolher' : 'Mostrar filtros ativos'}
												<FiChevronDown
													className={`h-4 w-4 transition-transform ${
														isHistoricalFiltersOpen ? 'rotate-180' : ''
													}`}
													aria-hidden='true'
												/>
											</button>

											{isHistoricalFiltersOpen && (
											<div className='flex flex-wrap items-center gap-2'>
										{(dataInicio || dataFim) && (
											<button
												type='button'
												onClick={resetHistoricalPeriod}
												className='inline-flex items-center gap-1 rounded-full border border-[#C9D9EC] bg-[#EAF2FC] px-2.5 py-1 text-xs text-[#13335A] hover:bg-[#DDEBFA]'>
												{dataInicio || 'início'} – {dataFim || 'fim'}
												<span aria-hidden='true'>×</span>
											</button>
										)}
										<button
											type='button'
											onClick={resetHistoricalAggregation}
											className='inline-flex items-center gap-1 rounded-full border border-[#C9D9EC] bg-[#EAF2FC] px-2.5 py-1 text-xs text-[#13335A] hover:bg-[#DDEBFA]'>
											Por {agregacao === 'mes' ? 'mês' : 'dia'}
											<span aria-hidden='true'>×</span>
										</button>
										{historicalFilterChips.map((chip) => (
											<button
												key={`${chip.filter}-${chip.value}`}
												type='button'
												onClick={() =>
													removeHistoricalFilter(
														chip.filter,
														chip.value,
													)
												}
												className='inline-flex max-w-full items-center gap-1 rounded-full border border-[#C9D9EC] bg-[#EAF2FC] px-2.5 py-1 text-left text-xs text-[#13335A] hover:bg-[#DDEBFA]'>
												<span className='truncate'>{chip.label}</span>
												<span aria-hidden='true'>×</span>
											</button>
										))}
											</div>
											)}
										</div>

									<div
										className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
											isHistoricalFiltersOpen
												? 'grid-rows-[1fr]'
												: 'grid-rows-[0fr]'
										}`}>
						<div
							className={`min-h-0 ${
								isHistoricalFiltersOpen
									? 'overflow-visible'
									: 'overflow-hidden'
							}`}>
											<div
												className={`border-t border-[#E8EDF4] px-4 py-5 sm:px-5 transition-opacity duration-200 ${
													isHistoricalFiltersOpen
														? 'opacity-100'
														: 'opacity-0'
												}`}>
									<div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end'>
										<div className='min-w-[100px] w-full'>
											<label className='block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2'>
												Data início
											</label>
											<input
												type='date'
												value={dataInicio}
											onChange={(e) => {
													setIsHistoricalPeriodDefault(false);
													setHistoricalAlert('');
													setDataInicio(e.target.value);
													loadHistoricoFilterOptions({
														dataInicio: e.target.value,
													});
												}}
												className={`w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm ${
													!dataInicio
														? 'text-[#E3E3E3]'
														: ''
												}`}
												placeholder='dd/mm/aaaa'
											/>
										</div>
										<div className='min-w-[100px] w-full'>
											<label className='block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2'>
												Data fim
											</label>
											<input
												type='date'
												value={dataFim}
											onChange={(e) => {
													setIsHistoricalPeriodDefault(false);
													setHistoricalAlert('');
													setDataFim(e.target.value);
													loadHistoricoFilterOptions({
														dataFim: e.target.value,
													});
												}}
												className={`w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm ${
													!dataFim
														? 'text-[#E3E3E3]'
														: ''
												}`}
												placeholder='dd/mm/aaaa'
											/>
										</div>

										<div className='w-full'>
											<label className='block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2'>
												Exibir por
											</label>
											<div className='flex w-full overflow-hidden rounded-md border border-[#C9D9EC] bg-[#EAF2FC] shadow-sm focus-within:ring-1 focus-within:ring-[#13335A]'>
												<label
													className={`flex flex-1 cursor-pointer items-center justify-center gap-1.5 px-2 py-1.5 text-xs transition-colors sm:gap-2 sm:py-2 sm:text-sm ${
														agregacao === 'dia'
															? 'bg-[#D9E9FA] font-semibold text-[#13335A]'
															: 'bg-[#EAF2FC] text-[#4F5E70] hover:bg-[#DDEBFA]'
													}`}>
													<input
														type='radio'
														name='exibir'
														value='dia'
														checked={
															agregacao === 'dia'
														}
												onChange={(e) =>
													handleHistoricalAggregationChange(e.target.value)
												}
														className={
															agregacao === 'dia'
																? 'accent-[#13335A]'
																: 'accent-[#13335A]'
														}
													/>
													<span>
														Dia
													</span>
												</label>
												<label
													className={`flex flex-1 cursor-pointer items-center justify-center gap-1.5 border-l border-[#C9D9EC] px-2 py-1.5 text-xs transition-colors sm:gap-2 sm:py-2 sm:text-sm ${
														agregacao === 'mes'
															? 'bg-[#D9E9FA] font-semibold text-[#13335A]'
															: 'bg-[#EAF2FC] text-[#4F5E70] hover:bg-[#DDEBFA]'
													}`}>
													<input
														type='radio'
														name='exibir'
														value='mes'
														checked={
															agregacao === 'mes'
														}
												onChange={(e) =>
													handleHistoricalAggregationChange(e.target.value)
												}
														className={
															agregacao === 'mes'
																? 'accent-[#13335A]'
																: 'accent-[#13335A]'
														}
													/>
													<span>
														Mês
													</span>
												</label>
											</div>
										</div>

										<SearchableMultiSelect
											label='Área Programática'
											options={historicalFilterOptions.area_programatica}
											selectedOptions={
												selectedHistoricalFilters.area_programatica
											}
											searchTerm={
												historicalFilterSearch.area_programatica || ''
											}
											isOpen={openHistoricalFilter === 'area_programatica'}
											onOpen={() => setOpenHistoricalFilter('area_programatica')}
											onClose={() =>
												setOpenHistoricalFilter((current) =>
													current === 'area_programatica' ? null : current,
												)
											}
											onSearchChange={(value) =>
												setHistoricalFilterSearch((previous) => ({
													...previous,
													area_programatica: value,
												}))
											}
											onToggleOption={(option) =>
												toggleHistoricalFilterOption(
													'area_programatica',
													option,
												)
											}
											labelClassName='mb-1 block text-xs font-medium text-gray-700 sm:mb-2 sm:text-sm'
										/>
										<SearchableMultiSelect
											label='Superintendência'
											options={historicalFilterOptions.superintendencia}
											selectedOptions={
												selectedHistoricalFilters.superintendencia
											}
											searchTerm={
												historicalFilterSearch.superintendencia || ''
											}
											isOpen={openHistoricalFilter === 'superintendencia'}
											onOpen={() => setOpenHistoricalFilter('superintendencia')}
											onClose={() =>
												setOpenHistoricalFilter((current) =>
													current === 'superintendencia' ? null : current,
												)
											}
											onSearchChange={(value) =>
												setHistoricalFilterSearch((previous) => ({
													...previous,
													superintendencia: value,
												}))
											}
											onToggleOption={(option) =>
												toggleHistoricalFilterOption(
													'superintendencia',
													option,
												)
											}
											labelClassName='mb-1 block text-xs font-medium text-gray-700 sm:mb-2 sm:text-sm'
										/>
										<SearchableMultiSelect
											label='Unidade'
											options={historicalFilterOptions.unidade}
											selectedOptions={selectedHistoricalFilters.unidade}
											searchTerm={historicalFilterSearch.unidade || ''}
											isOpen={openHistoricalFilter === 'unidade'}
											onOpen={() => setOpenHistoricalFilter('unidade')}
											onClose={() =>
												setOpenHistoricalFilter((current) =>
													current === 'unidade' ? null : current,
												)
											}
											onSearchChange={(value) =>
												setHistoricalFilterSearch((previous) => ({
													...previous,
													unidade: value,
												}))
											}
											onToggleOption={(option) =>
												toggleHistoricalFilterOption('unidade', option)
											}
											labelClassName='mb-1 block text-xs font-medium text-gray-700 sm:mb-2 sm:text-sm'
										/>
										<SearchableMultiSelect
											label='Tipos'
											options={historicalFilterOptions.tipo_secao}
											selectedOptions={selectedHistoricalFilters.tipo_secao}
											searchTerm={historicalFilterSearch.tipo_secao || ''}
											isOpen={openHistoricalFilter === 'tipo_secao'}
											onOpen={() => setOpenHistoricalFilter('tipo_secao')}
											onClose={() =>
												setOpenHistoricalFilter((current) =>
													current === 'tipo_secao' ? null : current,
												)
											}
											onSearchChange={(value) =>
												setHistoricalFilterSearch((previous) => ({
													...previous,
													tipo_secao: value,
												}))
											}
											onToggleOption={(option) =>
												toggleHistoricalFilterOption('tipo_secao', option)
											}
											labelClassName='mb-1 block text-xs font-medium text-gray-700 sm:mb-2 sm:text-sm'
										/>
										<SearchableMultiSelect
											label='Enfermarias'
											options={historicalFilterOptions.setor}
											selectedOptions={selectedHistoricalFilters.setor}
											searchTerm={historicalFilterSearch.setor || ''}
											isOpen={openHistoricalFilter === 'setor'}
											onOpen={() => setOpenHistoricalFilter('setor')}
											onClose={() =>
												setOpenHistoricalFilter((current) =>
													current === 'setor' ? null : current,
												)
											}
											onSearchChange={(value) =>
												setHistoricalFilterSearch((previous) => ({
													...previous,
													setor: value,
												}))
											}
											onToggleOption={(option) =>
												toggleHistoricalFilterOption('setor', option)
											}
											labelClassName='mb-1 block text-xs font-medium text-gray-700 sm:mb-2 sm:text-sm'
										/>

										<div className='lg:col-span-4 flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
											<p className='text-xs text-[#8B97A6]'>
												{historicalDaysCount} dias · {historicalPointCount} pontos · consulta independente do painel acima
											</p>
											<div className='flex gap-2'>
											<button
												onClick={
													handleApplySerieFilters
												}
												disabled={loadingSerie}
												className='flex-1 sm:flex-none px-3 sm:px-6 py-1.5 sm:py-2 bg-[#13335A] text-white rounded-md hover:bg-[#0d2440] transition-colors font-inter text-xs sm:text-[14px] font-normal disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap'>
												{loadingSerie
													? 'Carregando...'
													: 'Aplicar Filtros'}
											</button>
											<button
												onClick={() => {
													handleClearSerieFilters();
												}}
												disabled={loadingSerie}
												className='flex-1 sm:flex-none px-3 sm:px-[29px] py-1.5 sm:py-2 border border-gray-300 text-[#757575] rounded-md hover:bg-gray-50 transition-colors text-xs sm:text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_2px_2px_rgba(0,0,0,0.2)]'>
																Limpar
															</button>
														</div>
													</div>
												</div>
												</div>
											</div>
										</div>
									</div>

									{/* Área do Gráfico */}
									{historicalAlert && (
										<div
											role='alert'
											className='mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900'>
											{historicalAlert}
										</div>
									)}
									<div className='bg-white rounded-lg h-[300px] sm:h-[350px] md:h-[450px] border border-gray-200'>
										{loadingSerie ? (
											<div className='flex items-center justify-center h-full'>
												<p className='text-gray-400 text-xs sm:text-sm'>
													Carregando gráfico...
												</p>
											</div>
										) : serieData.serie &&
										  serieData.serie.length > 0 ? (
											<ReactECharts
												key={
													isMobile
														? 'mobile'
														: 'desktop'
												}
												option={getChartOption()}
												style={{
													height: '100%',
													width: '100%',
												}}
												notMerge={true}
												lazyUpdate={true}
												opts={{
													renderer: 'canvas',
													locale: 'PT-br',
												}}
											/>
										) : (
											<div className='flex items-center justify-center h-full'>
												<p className='text-gray-400 text-xs sm:text-sm'>
													Nenhum dado disponível para
													o período selecionado
												</p>
											</div>
										)}
									</div>
									{/* <p className='text-gray-700 text-[9px] leading-3'>
										{serieData?.nota_rodape}
									</p> */}
								</div>
							</div>
						)}

						{/* {activeTab === "boletins" && (
              <div className="bg-white p-8 rounded-lg shadow">
                <p className="text-gray-600">
                  Conteúdo de Boletins em desenvolvimento...
                </p>
              </div>
            )} */}

						{/* 	{activeTab === 'indicadores' && <IndicadoresTab />}

						{activeTab === 'doencas' && <DoencasAgravos />} */}
					</div>
				</div>
			)}
		</div>
	);
}

export default MonitoramentoSUBHUE;
